import { useEffect, useRef, useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import './ChatbotWidget.css';

const ChatbotWidget = () => {
  const { isOpen, messages, isLoading, closeChatbot, toggleChatbot, sendMessage } = useChatbot();
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (inputValue.trim() && !isLoading) {
      const message = inputValue.trim();
      setInputValue('');
      await sendMessage(message);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSend();
    }
  };

  return (
    <div id="chatbotWidget" className="chatbot-widget">
      <button
        id="chatbotToggle"
        className="chatbot-toggle"
        onClick={toggleChatbot}
        aria-label="Open Olitask assistant"
        aria-expanded={isOpen}
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
          <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
        </svg>
      </button>

      {isOpen && (
        <div id="chatbotWindow" className="chatbot-window">
          <div className="chatbot-header">
            <span>Olitask Assistant</span>
            <button id="chatbotClose" className="chatbot-close" onClick={closeChatbot}>×</button>
          </div>

          <div id="chatbotMessages" className="chatbot-messages">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`message-bubble ${msg.isUser ? 'user' : 'bot'}`}
                dangerouslySetInnerHTML={msg.html && !msg.isUser ? { __html: msg.text } : undefined}
              >
                {!msg.html && msg.text}
              </div>
            ))}
            {isLoading && (
              <div className="message-bubble bot typing">
                <span className="typing-dot">.</span>
                <span className="typing-dot">.</span>
                <span className="typing-dot">.</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chatbot-input-area">
            <input
              type="text"
              id="chatbotInput"
              className="chatbot-input"
              placeholder="Type your question..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              ref={inputRef}
              disabled={isLoading}
            />
            <button
              id="chatbotSend"
              className="chatbot-send"
              onClick={handleSend}
              disabled={isLoading}
            >
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatbotWidget;
